/**
 * De vensterlaag: één plek voor alle programmaatjes op het bureaublad.
 *
 * De Krabbelgotchi en de Krabbellijn hadden allebei hun eigen sleepcode, hun eigen
 * plekstijl, hun eigen kader en hun eigen titelbalk. Twee kopieën van hetzelfde,
 * regel voor regel, en de chat heeft er nog een derde, oudere variant van. Elke bug
 * moest je dus twee of drie keer repareren, en dat is één keer misgegaan: het kruisje
 * van de Krabbellijn werkte niet omdat de sleepcode de klik afving, terwijl dezelfde
 * fout bij het beestje al gefikst was.
 *
 * Hier staat het één keer. Een venster geeft zijn eigen naam mee en krijgt terug waar
 * het staat; de rest is voor alle vensters gelijk. Zie CLAUDE.md sectie 14 voor de
 * regel dat een venster met knoppen in zijn titelbalk alleen aan de balk sleept.
 *
 * Waar een venster staat wordt niet onthouden na herladen, en dat is met opzet: een
 * programmaatje dat morgen nog half buiten je scherm hangt omdat je toen een groter
 * beeld had, is erger dan een programmaatje dat gewoon weer in het midden begint.
 */

/**
 * Hoe hoog de vensters liggen.
 *
 * Boven de paal en de zijkolom, onder de taakbalk en onder de schermvullende lagen.
 * Sinds het tekenvenster erbij kwam kunnen er meerdere open staan en elkaar
 * overlappen, dus is er een stapelorde: het venster dat je het laatst aanraakte ligt
 * bovenop. Zie `vensterNaarVoren`.
 *
 * De twee getallen staan in lib/config.js, naast de hoogte van de taakbalk: die moet
 * erboven liggen en dan horen ze op één plek te staan.
 */
import { TASKBAR_H, Z_VENSTER, Z_VENSTER_MAX } from '../lib/config.js';

const VENSTER_Z = Z_VENSTER;
const VENSTER_Z_MAX = Z_VENSTER_MAX;

/** Hoeveel van een venster minstens in beeld blijft als je het wegsleept. */
const RAND = 4;

/**
 * Ligt er een beurt op je te wachten in de Krabbellijn?
 *
 * Dan staat zijn knop in de taakbalk, ook als het venster dicht is: dat is dan geen
 * draaiend programma maar een seintje, net als het bolletje bij de bel. De vraag zelf
 * staat in logic/ketting.js; hier alleen het net eromheen, want die functie rekent met
 * een keten die er nog niet hoeft te zijn.
 */
function ketBeurt(app) {
  try { return !!app.ketBeurt(); } catch (e) { return false; }
}
const KOP_ZICHTBAAR = 48;

export default {
  /**
   * Een venster oppakken aan zijn titelbalk.
   *
   * Niet op de telefoon: daar staat het venster vast tegen de randen en is slepen
   * alleen een manier om per ongeluk je scrollgebaar te verliezen.
   *
   * En niet als je op een knop drukt. In die balk staan het kruisje en de
   * geluidsknop, en `setPointerCapture` hieronder zou hun klik opeten.
   */
  vensterSleepAan(naam, e) {
      if (this.state.isMobile || !naam) return;
      if (e.target && typeof e.target.closest === 'function' && e.target.closest('button')) return;
      const el = e.currentTarget && e.currentTarget.closest('[data-venster]');
      if (!el) return;
      const r = el.getBoundingClientRect();
      this.vensterGreep = { naam, dx: e.clientX - r.left, dy: e.clientY - r.top, w: r.width };
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch (er) {}
      this.vensterZet(naam, { x: r.left, y: r.top });
    },

  vensterSleep(e) {
      const g = this.vensterGreep;
      if (!g) return;
      // Geen knop meer ingedrukt: de greep is blijven hangen, bijvoorbeeld omdat het
      // venster uit de pagina ging terwijl je sleepte en het loslaten nooit aankwam.
      // Dan schoof de plek mee zodra je daarna over een titelbalk bewoog.
      if (e.pointerType === 'mouse' && !e.buttons) { this.vensterSleepUit(); return; }
      // Binnen beeld houden: een venster dat half buiten het scherm hangt kun je
      // niet meer terugpakken, want de titelbalk is dan weg.
      const x = Math.max(RAND, Math.min(window.innerWidth - g.w - RAND, e.clientX - g.dx));
      // De balk hoort onaangeroerd te blijven: hij ligt er nu boven (Z_BALK), maar
      // een venster dat je er half achter schuift is nog steeds een venster dat je
      // niet meer kunt pakken.
      const y = Math.max(RAND, Math.min(window.innerHeight - TASKBAR_H - KOP_ZICHTBAAR, e.clientY - g.dy));
      this.vensterZet(g.naam, { x, y });
    },

  vensterSleepUit() { this.vensterGreep = null; },

  /** Waar dit venster staat vastleggen. */
  vensterZet(naam, plek) {
      return this.setState({
        vensterPlek: Object.assign({}, this.state.vensterPlek, { [naam]: plek })
      });
    },

  /**
   * Alle vensters die er zijn, en of er één open staat.
   *
   * Eén lijst, want drie dingen willen hetzelfde weten: de laag die het bureaublad
   * dempt (staat er iets open), Escape (welk venster ligt bovenop) en de taakbalk.
   * Stond dat op drie plekken los opgeschreven, dan vergeet je er één zodra er een
   * venster bij komt — en dat is precies wat er gebeurde toen het leaderbord en het
   * gastenboek vensters werden.
   */
  vensterLijst() {
      const w = (naam) => !!(this.state.vensterMin || {})[naam];
      /**
       * Per venster drie vragen en twee knoppen.
       *
       * `erin` is of hij in de taakbalk staat: het programma draait. `open` is of je
       * hem nu ziet; weggeklapt draait hij nog wel. `weg` klapt hem weg en `sluit`
       * doet hem echt dicht, en dan gaat zijn balkje uit de taakbalk.
       *
       * Het tekenvenster gaat via zijn eigen `minStudio`/`sluitStudio`, want daar
       * hangt het bewaren van het doek aan: eerst een momentopname, dan verbergen.
       * Zie logic/navigation.js. De rest gebruikt de twee generieke knoppen.
       */
      /**
       * `bestaat` is of het programma draait, `erin` of hij in de taakbalk staat en
       * `open` of je hem nu ziet. Meestal zijn de eerste twee hetzelfde; de
       * Krabbellijn is de uitzondering, want een openstaande beurt zet zijn knop in de
       * balk ook als het venster dicht is.
       */
      const gewoon = (naam, bestaat, erin) => ({
        naam,
        erin: erin === undefined ? bestaat : erin,
        open: bestaat && !w(naam),
        weg: () => this.vensterWeg(naam),
        terug: () => this.vensterTerug(naam)
      });
      return [
        Object.assign(gewoon('studio', !!this.state.studioOpen), {
          // Terug via openStudio en niet via de wegklapkaart: het tekenvenster klapt weg
          // met zijn eigen `studioMin`, dus de generieke `vensterTerug` zette een vlag
          // om die dit venster niet leest. Het balkje in de taakbalk deed daardoor
          // niets meer zodra je het doek had weggeklapt.
          open: this.tekentNu(), weg: this.minStudio, terug: this.openStudio, sluit: this.sluitStudio
        }),
        // De chat hangt in het systeemvak en niet bij de programma's; hij heeft daar
        // een vast tekentje dat nooit weggaat, dus voor hem is wegklappen hetzelfde
        // als dichtdoen. Zie de kop van views/screens/Taskbar.jsx.
        { naam: 'chat', erin: false, open: this.chatVisible(), weg: this.toggleChat, sluit: this.toggleChat },
        // Deze twee tekenen op een canvas, en dat is leeg als het venster terugkomt.
        // Het beestje krijgt daarbij zijn klok terug, want die staat stil zolang hij
        // weggeklapt is; zie tamKlok.
        Object.assign(gewoon('beest', !!this.state.beestOpen), {
          sluit: this.closeTamagotchi,
          terug: () => this.vensterTerug('beest', () => { this.tamKlok(); this.tamTeken(); })
        }),
        Object.assign(gewoon('ket', !!this.state.ketOpen,
          this.magBeta() && (!!this.state.ketOpen || !!ketBeurt(this))), {
          sluit: this.closeKetting,
          // Weggeklapt zwijgt het deuntje, net zoals de Krabbelgotchi dan niet tikt.
          weg: () => { this.vensterWeg('ket'); this.ketMuziek(); },
          // Staat hij alleen in de balk omdat er een beurt op je wacht, dan is er geen
          // venster om terug te halen: dan hoort de knop het spel te openen.
          terug: () => (this.state.ketOpen
            ? this.vensterTerug('ket', () => { this.ketTeken(); this.ketMuziek(); })
            : this.openKetting())
        }),
        Object.assign(gewoon('arcade', !!this.state.arcadeOpen), { sluit: this.closeArcade }),
        Object.assign(gewoon('activity', !!this.state.activityOpen), { sluit: this.closeActivity }),
        Object.assign(gewoon('gastenboek', !!this.state.gbOpen), { sluit: this.closeGuestbook }),
        // Alleen als die krabbel er ook echt is: een link naar een verwijderde krabbel
        // zette `viewerId` wel. En niet in de taakbalk: een krabbel bekijken is geen
        // programma dat draait, het is een plaatje dat je even opendoet. Daarom heeft
        // hij ook geen wegklapknop, want er is geen balkje om hem terug te halen.
        Object.assign(gewoon('viewer', !!this.state.viewerId
          && (this.state.galleryItems || []).some((g) => g.id === this.state.viewerId), false), { sluit: this.closeViewer })
      ];
    },

  /** Staat er een venster open? */
  vensterOpen() { return this.vensterLijst().some((x) => x.open); },

  /** Eén venster, op naam. */
  vensterInfo(naam) { return this.vensterLijst().find((x) => x.naam === naam) || null; },

  /**
   * Wegklappen: het venster uit beeld, het programma blijft draaien.
   *
   * Dat verschil is het hele punt van een taakbalk. Het kruisje doet hem dicht en
   * haalt zijn balkje weg; deze knop laat hem staan zodat je met één klik terug bent.
   */
  vensterWeg(naam) {
      return this.setState({ vensterMin: Object.assign({}, this.state.vensterMin, { [naam]: true }) });
    },

  /**
   * En terug, vooraan.
   *
   * `na` draait zodra het venster weer in de pagina staat. Weggeklapt wordt een
   * venster uit de pagina gehaald, en een `<canvas>` komt dan leeg terug: wat erop
   * stond moet opnieuw getekend worden. Zie de Krabbelgotchi en de Krabbellijn in
   * vensterLijst.
   */
  vensterTerug(naam, na) {
      this.vensterNaarVoren(naam);
      return this.setState({ vensterMin: Object.assign({}, this.state.vensterMin, { [naam]: false }) }, na);
    },

  /**
   * De knop in de taakbalk: een schakelaar.
   *
   * Zie je het venster en ligt het vooraan, dan klap je het weg. Ligt het weggeklapt
   * of achteraan, dan komt het terug en naar voren. Net als in een echte taakbalk, en
   * hetzelfde wat `uitBalkStudio` voor het tekenvenster deed; die functie is weg.
   */
  vensterUitBalk(naam) {
      const w = this.vensterInfo(naam);
      if (!w) return undefined;
      if (w.open && this.vensterVoorop() === naam) return w.weg();
      if (w.open) return this.vensterNaarVoren(naam);
      return w.terug ? w.terug() : this.vensterTerug(naam);
    },

  /** Waar dit venster staat, of niets als je het nog niet hebt verplaatst. */
  vensterPlekVan(naam) { return (this.state.vensterPlek || {})[naam] || null; },

  /**
   * Dit venster bovenop leggen.
   *
   * De orde is een rijtje namen, achteraan ligt bovenop. Raak je een venster aan, dan
   * gaat zijn naam naar achteren; de rest schuift een plek op. Zo hoef je nergens een
   * nummer bij te houden en klopt het altijd, ook als er een venster tussenuit valt.
   *
   * Dit hangt aan `onPointerDownCapture` op het hele venster en niet alleen aan de
   * titelbalk: een venster naar voren halen doe je door erin te klikken, waar dan ook,
   * en die klik moet daarna gewoon zijn werk doen. Vandaar `capture` en geen `stop`.
   */
  vensterNaarVoren(naam) {
      if (!naam || this.vensterVoorop() === naam) return;
      const orde = (this.state.vensterOrde || []).filter((n) => n !== naam);
      orde.push(naam);
      // Acht is meer dan er ooit open staan; langer laten groeien heeft geen zin.
      this.setState({ vensterOrde: orde.slice(-8) });
    },

  /** Welk venster nu bovenop ligt, of niets. */
  vensterVoorop() {
      const orde = this.state.vensterOrde || [];
      // Het achterste van de open vensters, niet het achterste van de lijst. Sloot of
      // klapte je het voorste weg, dan bleef zijn naam achteraan staan en tekenden alle
      // andere vensters zich als inactief tot je er een aanklikte.
      const open = new Set(this.vensterLijst().filter((w) => w.open).map((w) => w.naam));
      for (let i = orde.length - 1; i >= 0; i--) if (open.has(orde[i])) return orde[i];
      return '';
    },

  /** Hoe hoog dit venster ligt: achteraan in de orde is bovenop. */
  vensterZ(naam) {
      /**
       * Tellen onder de open vensters, niet in de hele orde.
       *
       * De orde onthoudt ook vensters die dicht zijn, en tussen 91 en 95 passen er vijf.
       * Had je er in één sessie zes aangeraakt, dan kregen de bovenste allemaal 95 en
       * besliste de volgorde in de pagina wie er bovenop lag: klikken op het onderste
       * deed niets, terwijl zijn schaduw "vooraan" zei.
       */
      const open = new Set(this.vensterLijst().filter((w) => w.open).map((w) => w.naam));
      const orde = (this.state.vensterOrde || []).filter((n) => open.has(n));
      const i = orde.indexOf(naam);
      if (i < 0) return VENSTER_Z;
      // Het bovenste krijgt altijd de top; wat eronder ligt telt terug en houdt bij
      // meer dan vijf open vensters de onderste op de bodem.
      return Math.max(VENSTER_Z + 1, VENSTER_Z_MAX - (orde.length - 1 - i));
    },

  /**
   * Waar het venster komt te staan.
   *
   * Op de telefoon tegen de onderrand van links naar rechts, of schermvullend met
   * `vol`. Op een computer waar je hem hebt neergezet, en zolang je hem nog niet hebt
   * aangeraakt midden op het werkvlak.
   *
   * @param breed  de breedte als css-waarde, bijvoorbeeld '380px'
   */
  vensterStaStyle(naam, breed, vol) {
      if (this.state.isMobile) {
        // `vol`: op de telefoon vult dit venster het beeld, met tien pixels lucht
        // rondom zoals elk schermvullend venster hier. Dat is voor de vensters die
        // een lap inhoud hebben (het leaderbord, het gastenboek, een krabbel); de
        // spelletjes blijven onderaan staan, want die zijn zo hoog als hun schermpje.
        if (vol) return { position: 'fixed', inset: '10px', zIndex: String(this.vensterZ(naam)) };
        return { position: 'fixed', left: '8px', right: '8px', bottom: '8px', zIndex: String(this.vensterZ(naam)) };
      }
      const neer = this.vensterPlekVan(naam);
      /**
       * En binnen het huidige beeld, niet dat van toen je hem neerzette.
       *
       * Slepen houdt hem binnen het scherm, maar maak je daarna je browser smaller, dan
       * stond hij er voorbij en was de titelbalk niet meer te pakken tot je herlaadde.
       * Er blijft altijd een stuk titelbalk over om hem aan terug te halen.
       */
      const plek = neer && {
        x: Math.max(RAND, Math.min(neer.x, window.innerWidth - KOP_ZICHTBAAR * 3)),
        y: Math.max(RAND, Math.min(neer.y, window.innerHeight - TASKBAR_H - KOP_ZICHTBAAR))
      };
      return plek
        ? { position: 'fixed', left: plek.x + 'px', top: plek.y + 'px', width: breed, zIndex: String(this.vensterZ(naam)) }
        : {
          /**
           * Midden op het werkvlak, tot je hem zelf ergens neerzet.
           *
           * Midden in de ruimte bóven de taakbalk en niet midden in het scherm: de
           * balk is vierenzestig hoog, dus anders staat elk venster tweeëndertig
           * pixels te laag en ligt de onderrand van een hoog venster achter de
           * startknop. Hier stond `bottom: TASKBAR_H + 12`, tegen de balk aan; dat
           * las als een lade die uitschuift en niet als een venster dat opengaat.
           *
           * En met marges op `auto` in plaats van met een transform. Dat is geen
           * smaak: **een transform maakt van dit venster het referentievlak voor elke
           * `position: fixed` erbinnen.** Met `translate(-50%,-50%)` hing het lijstje
           * "Geliket door" niet meer aan het scherm maar aan het venster, en stond het
           * precies de vensterpositie ernaast: honderdvierenveertig pixels naar rechts
           * en twaalf omlaag, nagemeten. Vier insets op nul met `margin: auto` doet
           * hetzelfde centreren zonder die bijwerking.
           *
           * `height: max-content` hoort erbij: auto-marges centreren alleen een doos
           * met een bepaalde hoogte, en die haalt hij zo uit zijn inhoud. De
           * bovengrens komt van `vensterKaderStyle`, dus hij kan nooit hoger worden
           * dan het werkvlak en dit midden valt nooit buiten beeld.
           */
          position: 'fixed', left: '0', right: '0', top: '0', bottom: TASKBAR_H + 'px',
          margin: 'auto', height: 'max-content',
          width: breed, zIndex: String(this.vensterZ(naam))
        };
    },

  /**
   * Het kader van een venster.
   *
   * Lila, dezelfde plaat als de behuizing van Krabbelworm en Memory, met de rand van
   * vier en de schaduw van acht die elk venster in deze app heeft. Eén kader en de
   * inhoud staat er direct in: een venster in een venster is diepte in diepte en dat
   * hoort hier niet. Zie sectie 5 van CLAUDE.md.
   *
   * @param maxHoog  hoe hoog hij mag worden; wat er niet in past scrolt binnenin
   */
  vensterKaderStyle(maxHoog, voorop, vlak) {
      /**
       * Nooit hoger dan wat er boven de taakbalk over is.
       *
       * De vensters geven hun eigen bovengrens mee, en die stond in `dvh`: de
       * Krabbelgotchi op 90dvh, de Krabbellijn op 88dvh. Samen met de onderrand die
       * `TASKBAR_H + 12` boven de schermrand ligt komt dat op een scherm van minder
       * dan 760 pixels hoog boven de honderd procent uit, en dan steekt de titelbalk
       * aan de bovenkant het beeld uit. Precies de balk waaraan je hem moet pakken.
       *
       * Hier en niet in elk venster apart: de laag weet dat er een taakbalk is, een
       * spelletje hoeft dat niet te weten. Op de telefoon niet, want daar staan de
       * vensters met opzet over de balk heen en vullen ze het beeld.
       */
      const grens = this.state.isMobile
        ? maxHoog
        : 'min(' + maxHoog + ', calc(100dvh - ' + (TASKBAR_H + 24) + 'px))';
      return {
        /**
         * Lila voor een apparaatje, wit voor een document.
         *
         * De spelletjes zijn een apparaat met een schermpje erin, en die behuizing is
         * lila (zie sectie 5 van CLAUDE.md). Het leaderbord, het gastenboek en een
         * krabbel zijn geen apparaat maar een blad: die krijgen wit, en dan staat de
         * inhoud er direct op in plaats van in een tweede kader erbinnen.
         */
        background: vlak || 'var(--lila-diep)', border: '4px solid var(--ink)', borderRadius: '16px',
        /**
         * Het venster dat vooraan ligt heeft een diepere schaduw.
         *
         * Acht als hij vooraan ligt, vier als hij eronder ligt. Zo zie je in één blik
         * welk venster je aanraakt zonder dat er een tweede kleur of een extra rand
         * bij komt; het is dezelfde schaduw, alleen korter. Zie sectie 5 van CLAUDE.md
         * voor de regel dat er niet meer dan twee schaduwmaten in de app zijn.
         */
        boxShadow: (voorop ? '8px 8px 0 ' : '4px 4px 0 ') + 'var(--ink)', overflow: 'hidden',
        display: 'flex', flexDirection: 'column', maxHeight: grens
      };
    },

  /** De titelbalk is de handgreep. Roze, net als de kop van de app zelf. */
  vensterBalkStyle(voorop, rand) {
      /**
       * Opzij net zoveel lucht als de inhoud eronder.
       *
       * Hier stond vast 14 links en 10 rechts, en de meeste vensters hebben 24 padding:
       * de titel begon dan tien pixels links van de inhoud en het kruisje eindigde
       * veertien pixels voorbij de rechterrand ervan. Nagemeten in het leaderbord, de
       * hal, het gastenboek en de viewer. `rand` is dezelfde padding als die van de
       * inhoud; het knoppensetje trekt zichzelf zes naar rechts (vensterKnopRijStyle),
       * zodat het tekentje van het kruisje precies op die rand eindigt.
       */
      const r = typeof rand === 'number' ? rand : 14;
      return {
        display: 'flex', alignItems: 'center', gap: '10px', flex: '0 0 auto',
        padding: '9px ' + r + 'px 9px ' + r + 'px', boxSizing: 'border-box',
        // De balk van een venster dat achteraan ligt is bleker, zoals een titelbalk
        // hoort te doen als het venster niet actief is.
        background: voorop || this.state.isMobile ? 'var(--roze-balk)' : 'var(--lila)',
        borderBottom: '4px solid var(--ink)',
        cursor: this.state.isMobile ? 'default' : 'grab', touchAction: 'none', userSelect: 'none'
      };
    },

  vensterTitelStyle() {
      return { fontSize: '15px', fontWeight: 700, flex: '1 1 auto', minWidth: '0' };
    },

  /**
   * De knoppen rechts in de titelbalk: wegklappen, groot maken, sluiten.
   *
   * Ze staan dichter op elkaar dan de knoppen in de rest van de app, en dat is met
   * opzet: het is één setje van twee of drie dat bij het venster hoort en niet bij de
   * inhoud. Ze stonden op `sluitStyle`, met elf pixels padding rondom, en dan liggen
   * de vakjes wel tegen elkaar maar zit er eenentwintig pixels dode ruimte tussen de
   * tekentjes. Nagemeten in de titelbalk; nu is dat elf.
   *
   * Zes horizontaal en elf verticaal. De negatieve marge staat alléén verticaal: die
   * houdt het tikdoel 42 hoog zonder de balk op te rekken. Horizontaal mag hij niet,
   * en dat is een fout die ik eerst maakte: met `margin: -11px -6px` overlappen twee
   * knoppen naast elkaar twaalf pixels, en dan ligt het kruisje over een derde van het
   * tikdoel van het streepje. De tekentjes raakten elkaar, wat er strak uitzag maar
   * betekende dat je het streepje aan de rechterkant niet meer kon raken.
   *
   * Het tikdoel is dus 32 bij 42 en de tekentjes staan twaalf pixels van elkaar. Dat
   * 32 smaller is dan de vierenveertig van sectie 7 kan hier: de balk is maar 46 hoog,
   * dus vierenveertig in het vierkant was er nooit, en de knoppen in de taakbalk staan
   * om dezelfde reden op 42.
   *
   * `flex-start` en niet `center`: de titel mag over twee regels lopen (een lange
   * krabbeltitel doet dat), en dan horen de knoppen bovenaan te blijven staan.
   */
  vensterKnopStyle() {
      return {
        alignSelf: 'flex-start', flex: '0 0 auto',
        border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--ink)',
        display: 'flex', padding: '11px 6px', margin: '-11px 0', borderRadius: '12px'
      };
    },

  /**
   * Het groepje eromheen.
   *
   * Zonder groepje krijgt elke knop het gat van tien uit de balk, ook onderling. In
   * een eigen rij met `gap: 0` staat dat gat alleen nog tussen de titel en het setje,
   * waar het hoort.
   */
  vensterKnopRijStyle() {
      return {
        display: 'flex', alignItems: 'flex-start', gap: '0', flex: '0 0 auto',
        // Zes naar rechts, zodat het laatste tekentje net zo ver van de vensterrand
        // blijft als toen er één knop stond met elf padding en elf negatieve marge.
        marginLeft: 'auto', marginRight: '-6px'
      };
    },

  /**
   * Alles wat een venster van deze laag nodig heeft, in één keer.
   *
   * Zo staat er in de vals van een venster één regel in plaats van zes, en kan er
   * later iets bij zonder dat elk venster daar apart van hoort te weten.
   *
   * @param opties  `vlak` zet de kleur van het kader (wit voor een document),
   *                `vol` maakt hem schermvullend op de telefoon, `rand` is de padding
   *                opzij van de inhoud, zodat de titelbalk daarmee uitlijnt
   */
  vensterVals(naam, breed, maxHoog, opties) {
      const o = opties || {};
      return {
        naam,
        /**
         * Kun je hem wegklappen? Alleen als er een balkje is om hem terug te halen.
         *
         * Op de telefoon staan alleen de Krabbelgotchi en de Krabbellijn in de balk.
         * Het leaderbord, het gastenboek, de hal en een krabbel verdwenen na wegklappen
         * zonder weg terug, en een krabbel kon je daarna niet eens meer openen.
         */
        kanWeg: naam !== 'viewer' && (!this.state.isMobile || naam === 'beest' || naam === 'ket'),
        staStyle: this.vensterStaStyle(naam, breed, o.vol),
        kaderStyle: this.vensterKaderStyle(maxHoog, this.vensterVoorop() === naam, o.vlak),
        balkStyle: this.vensterBalkStyle(this.vensterVoorop() === naam, o.rand),
        titelStyle: this.vensterTitelStyle(),
        knopStyle: this.vensterKnopStyle(),
        knopRijStyle: this.vensterKnopRijStyle(),
        sleepAan: (e) => this.vensterSleepAan(naam, e),
        sleep: this.vensterSleep,
        sleepUit: this.vensterSleepUit,
        // Erin klikken haalt hem naar voren, waar je ook klikt. In de opvangfase,
        // zodat de klik daarna gewoon zijn eigen werk doet.
        naarVoren: () => this.vensterNaarVoren(naam),
        voorop: this.vensterVoorop() === naam,
        // De twee knoppen in de titelbalk. Wegklappen laat het programma draaien en
        // zijn balkje staan; sluiten haalt beide weg.
        weg: () => { const w = this.vensterInfo(naam); if (w) w.weg(); },
        sluit: () => { const w = this.vensterInfo(naam); if (w && w.sluit) w.sluit(); }
      };
    }
};
